import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge, Icon, MobileHeader, Segmented, EmptyState } from '@/components';
import { useTestReview } from '@/hooks/queries';
import type { TestReviewQuestion } from '@/types/domain';

const LETTERS = ['ก', 'ข', 'ค', 'ง', 'จ', 'ฉ'];
const isCorrect = (q: TestReviewQuestion, oi: number) => (Array.isArray(q.answer) ? q.answer.includes(oi) : q.answer === oi);

function ReviewCard({ q, n }: { q: TestReviewQuestion; n: number }) {
  return (
    <div className="card" style={{ padding: 18 }}>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span className="u-mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>ข้อ {n}</span>
        {q.image && <Badge kind="slate">EKG</Badge>}
      </div>
      <div className="th" style={{ fontSize: 15, fontWeight: 500, lineHeight: 1.45, marginBottom: 12 }}>{q.stem}</div>
      {q.image && <img src={q.image} alt="" style={{ display: 'block', maxWidth: '100%', borderRadius: 10, border: '1px solid var(--line)', margin: '0 0 12px' }} />}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        {q.options.map((opt, oi) => {
          const ok = isCorrect(q, oi);
          return (
            <div key={oi} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '9px 12px', borderRadius: 9, border: `1.5px solid ${ok ? 'var(--sage)' : 'var(--line)'}`, background: ok ? 'var(--sage-tint)' : 'transparent' }}>
              <span className="u-mono" style={{ fontSize: 12.5, color: ok ? 'var(--sage-deep)' : 'var(--ink-3)', width: 16, flex: 'none' }}>{LETTERS[oi] ?? oi + 1}</span>
              <span className="th" style={{ fontSize: 14, flex: 1, fontWeight: ok ? 500 : 400 }}>{opt}</span>
              {ok && <Icon name="check" size={15} style={{ color: 'var(--sage)', flex: 'none' }} />}
            </div>
          );
        })}
      </div>
      {q.rationale && (
        <div className="th" style={{ marginTop: 12, padding: '10px 12px', background: 'var(--card-2)', borderRadius: 9, fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.5 }}>{q.rationale}</div>
      )}
    </div>
  );
}

/**
 * Learner-facing answer review — the same answer-key view the instructor sees,
 * but reveal-on-pass: the RPC throws `not_unlocked` (→ query error) until the
 * learner has taken the pre-test / passed the post-test.
 */
export function LearnerReview() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [kind, setKind] = useState<'pre' | 'post'>('post');
  const query = useTestReview(id, kind);
  const review = query.data;

  return (
    <div style={{ background: 'var(--paper)', minHeight: '100%' }}>
      <MobileHeader title="Review answers" th="เฉลยข้อสอบ" onBack={() => navigate(`/learn/learning/${id}`)} large={false} />
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '16px 20px 40px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Segmented value={kind} onChange={setKind} options={[{ value: 'pre' as const, label: 'Pre-test' }, { value: 'post' as const, label: 'Post-test' }]} />

        {query.isError ? (
          <EmptyState
            icon="lock"
            title={kind === 'post' ? 'Pass the post-test to see the answers' : 'Take the pre-test first'}
            hint={kind === 'post' ? 'The answer key unlocks once you score 80% or higher.' : 'Your pre-test sets your baseline — it unlocks here once taken.'}
          />
        ) : query.isLoading ? (
          <div className="card" style={{ padding: 40, textAlign: 'center' }}><span className="eyebrow">Loading…</span></div>
        ) : review ? (
          <>
            <div className="card" style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{kind === 'post' ? 'Post-test' : 'Pre-test'} · เฉลยพร้อมคำอธิบาย</div>
                <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 2 }}>{review.questions.length} questions · correct answers in green</div>
              </div>
              <Badge kind="sage" dot>Your score {review.score}%</Badge>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {review.questions.map((q, n) => <ReviewCard key={q.id} q={q} n={n + 1} />)}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
